import React from "react";
import {
  Col,
  Row,
  ModalBody,
  Label,
  Input,
  Modal,
  ModalHeader,
  Form,
  ModalFooter,
  Button,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { useFormik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { handleChange } from "../../service/CommonService";

/* ---------- Types ---------- */

interface ReasonFormValues {
  reason: string;
  check: boolean;
}

export interface ReasonSubmitPayload {
  id: number | null;
  reason: string;
}

interface ReasonProps {
  isOpen: boolean;
  toggle: () => void;
  action: string;
  id?: number | null;
  click: (reasonPayload: ReasonSubmitPayload) => void;
  isLoading?: boolean;
  zIndex?: number;
}

/* ---------- Component ---------- */

const Reason: React.FC<ReasonProps> = ({
  isOpen,
  toggle,
  action,
  id,
  click,
  isLoading = false,
  zIndex = 1080,
}) => {
  /* ---------- Formik ---------- */

  const formik = useFormik<ReasonFormValues>({
    initialValues: {
      reason: "",
      check: false,
    },
    validationSchema: Yup.object({
      reason: Yup.string()
        .trim()
        .required("Reason is required.")
        .max(250, "Reason cannot exceed 250 characters."),

      check: Yup.boolean().oneOf([true], "Please confirm checked."),
    }),
    onSubmit: async (
      values: ReasonFormValues,
      _helpers: FormikHelpers<ReasonFormValues>,
    ): Promise<void> => {
      if (id === undefined) return;
      const obj: ReasonSubmitPayload = {
        id,
        reason: values.reason.trim(),
      };
      click(obj);
    },
  });

  /* ---------- Render ---------- */

  return (
    <Modal
      id="showModal"
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="lg"
      backdrop="static"
      zIndex={zIndex}
      onClosed={() => formik.resetForm()}
    >
      <ModalHeader className="p-3 border-bottom" toggle={toggle}>
        User Verification
      </ModalHeader>

      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row className="g-3">
            <Col lg={12}>
              <div>
                <Label htmlFor="reason" className="form-label">
                  Reason <span className="text-danger">*</span>
                </Label>

                <Input
                  type="textarea"
                  id="reason"
                  name="reason"
                  value={formik.values.reason}
                  onChange={(e) => handleChange(formik, e)}
                  onBlur={formik.handleBlur}
                  invalid={Boolean(
                    formik.touched.reason && formik.errors.reason,
                  )}
                  placeholder="Enter reason..."
                />

                {formik.touched.reason && formik.errors.reason && (
                  <FormFeedback>{formik.errors.reason}</FormFeedback>
                )}
              </div>
            </Col>

            <Col lg={12}>
              <div className="form-check">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  name="check"
                  checked={formik.values.check}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={Boolean(formik.touched.check && formik.errors.check)}
                  id="check"
                />

                <Label htmlFor="check" className="form-check-label">
                  I confirm that to the best of my knowledge it accurately
                  describes the relevant documents information.
                </Label>

                {formik.touched.check && formik.errors.check && (
                  <FormFeedback>{formik.errors.check}</FormFeedback>
                )}
              </div>
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter className="border-top pt-2 pb-2">
          <div className="hstack gap-2 justify-content-end">
            <Button
              outline
              color="light"
              onClick={toggle}
              className="w-sm"
              type="button"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              color="primary"
              id="add-btn"
              className="w-sm"
              disabled={isLoading}
            >
              {isLoading && <Spinner size="sm" className="me-2" />}
              {action}
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default Reason;
